// CAROUSEL COMPONENT
class CarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.querySelector('.js-carousel');
    this.carouselItems = this.querySelectorAll('.js-carousel-item');
    this.prevButton = this.querySelector('.js-carousel-button-prev');
    this.nextButton = this.querySelector('.js-carousel-button-next');
    this.currentIndex = 0;
    this.itemWidth = 0;
    this.gap = 0;

    if (!this.carousel || !this.prevButton || !this.nextButton) return;

    this.init();
    this.prevButton.addEventListener('click', () => this.onButtonClick('prev'));
    this.nextButton.addEventListener('click', () => this.onButtonClick('next'));

    const resizeObserver = new ResizeObserver(() => this.init());
    resizeObserver.observe(this.carousel);
  }

  init() {
    if (this.carouselItems.length > 0) {
      // Width of one item
      this.itemWidth = this.carouselItems[0].offsetWidth;

      // get computed gap from CSS
      const style = window.getComputedStyle(this.carousel);
      this.gap = Number.isFinite(parseFloat(style.columnGap || style.gap))
        ? parseFloat(style.columnGap || style.gap)
        : 0;
    }
  }

  onButtonClick(direction) {
    const visibleCount = Math.floor(this.carousel.offsetWidth / (this.itemWidth + this.gap));
    const lastIndex = this.carouselItems.length - visibleCount;

    if (direction === 'next') {
      this.currentIndex = this.currentIndex < lastIndex ? this.currentIndex + 1 : 0;
    } else {
      this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : lastIndex;
    }

    this.scrollToCurrent();
  }

  scrollToCurrent() {
    const offset = this.currentIndex * (this.itemWidth + this.gap);
    this.carousel.scrollTo({
      left: offset,
      behavior: 'smooth', // smooth animation
    });
  }
}
customElements.define('carousel-component', CarouselComponent);

// CAROUSEL CATEGORIES COMPONENT
class CarouselCategoriesComponent extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.querySelector('.js-carousel-categories');
    this.carouselWrapper = this.querySelector('.js-carousel-categories-wrapper');
    this.prevButton = this.querySelector('.js-carousel-categories-button-prev');
    this.nextButton = this.querySelector('.js-carousel-categories-button-next');
    this.scrollSize = 400;

    if (!this.carousel || !this.carouselWrapper || !this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', () => this.onButtonClick('prev'));
    this.nextButton.addEventListener('click', () => this.onButtonClick('next'));

    this.carousel.addEventListener('scroll', () => {
      this.updateButtonsVisibility();
    });

    this.init();
  }

  init() {
    this.updateButtonsVisibility();
  }

  onButtonClick(direction) {
    const offsetDirection = direction === 'next' ? 1 : -1;
    const offset = this.scrollSize * offsetDirection;
    this.carousel.scrollTo({
      left: this.carousel.scrollLeft + offset,
      behavior: 'smooth', // smooth animation
    });
  }

  updateButtonsVisibility() {
    const scrollLeft = this.carousel.scrollLeft;
    const maxScrollLeft = this.carousel.scrollWidth - this.carousel.clientWidth;

    // Si on est tout à gauche
    if (scrollLeft <= 0) {
      this.prevButton.style.display = 'none';
    } else {
      this.prevButton.style.display = '';
    }

    // Si on est tout à droite
    if (scrollLeft >= maxScrollLeft - 1) {
      this.nextButton.style.display = 'none';
    } else {
      this.nextButton.style.display = '';
    }
  }
}
customElements.define('carousel-categories-component', CarouselCategoriesComponent);

// CAROUSEL FULL COMPONENT
class CarouselFullComponent extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.querySelector('.js-carousel');
    this.carouselItems = this.querySelectorAll('.js-carousel-item');
    this.prevButton = this.querySelector('.js-carousel-button-prev');
    this.nextButton = this.querySelector('.js-carousel-button-next');
    this.dots = this.querySelectorAll('.carousel-dot');
    this.currentIndex = 0;
    this.itemWidth = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.delay = parseInt(this.dataset.delay, 10) || 5; // default 5 sec
    this.interval = null;

    if (!this.carousel || !this.prevButton || !this.nextButton) return;

    this.init();
    this.prevButton.addEventListener('click', () => this.onButtonClick('prev'));
    this.nextButton.addEventListener('click', () => this.onButtonClick('next'));
    this.carousel.addEventListener('scroll', () => {
      const index = Math.round(this.carousel.scrollLeft / this.carousel.offsetWidth);
      this.currentIndex = index;
      this.updateDots();
    });
    this.carousel.addEventListener('mouseenter', () => this.stopAutoplay());
    this.carousel.addEventListener('mouseleave', () => this.startAutoplay());
    this.carousel.addEventListener('touchstart', () => this.stopAutoplay());
    this.carousel.addEventListener('touchend', () => this.startAutoplay());
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.currentIndex = parseInt(dot.dataset.index, 10);
        this.scrollToCurrent();
        this.startAutoplay();
      });
    });

    this.startAutoplay();

    const resizeObserver = new ResizeObserver(() => this.init());
    resizeObserver.observe(this.carousel);
  }

  init() {
    this.itemWidth = this.carousel.offsetWidth;
    this.updateDots();
  }

  onButtonClick(direction) {
    this.stopAutoplay();

    const lastIndex = this.carouselItems.length - 1;

    if (direction === 'next') {
      this.currentIndex = this.currentIndex < lastIndex ? this.currentIndex + 1 : 0;
    } else {
      this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : lastIndex;
    }

    this.scrollToCurrent();
    this.startAutoplay(); // restart
  }

  scrollToCurrent() {
    this.carousel.scrollTo({
      left: this.currentIndex * this.carousel.offsetWidth,
      behavior: 'smooth',
    });
  }

  startAutoplay() {
    if (!this.autoplay) return;

    this.stopAutoplay(); // safety

    this.interval = setInterval(() => {
      this.nextSlide();
    }, this.delay * 1000);
  }

  stopAutoplay() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  nextSlide() {
    const lastIndex = this.carouselItems.length - 1;
    this.currentIndex = this.currentIndex < lastIndex ? this.currentIndex + 1 : 0;
    this.scrollToCurrent();
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('!bg-blue', '!w-5'); // active style
      } else {
        dot.classList.remove('!bg-blue', '!w-5');
      }
    });
  }
}
customElements.define('carousel-full-component', CarouselFullComponent);

/**
 * MODAL
 */
class Modal extends HTMLElement {
  constructor() {
    super();
    this.buttonClose = this.querySelector('.js-modal-close');
    this.overlay = this.querySelector('.js-modal-overlay');

    if (this.buttonClose) {
      this.buttonClose.addEventListener('click', () => this.close());
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
  }

  show() {
    document.body.classList.add('overflow-hidden', 'h-screen');
    this.setAttribute('open', '');
  }

  close() {
    document.body.classList.remove('overflow-hidden', 'h-screen');
    this.removeAttribute('open');
  }
}

/**
 * MODAL OPENER
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const targetClass = '.' + this.getAttribute('data-modal');
      const modal = document.querySelector(targetClass);
      if (modal) modal.show();
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 * MODAL - PRODUCT SLIDESHOW
 */
class ModalProductSlideshow extends Modal {
  constructor() {
    super();
    this.mainMediaWrapper = this.querySelector('.js-main-media-wrapper');
    this.mainMedia = this.querySelector('.js-main-media');
    this.buttonZoomIn = this.querySelector('.js-button-zoom-in');
    this.buttonZoomOut = this.querySelector('.js-button-zoom-out');
    this.previews = this.querySelectorAll('.js-preview-media');
    this.loader = this.querySelector('.js-loader');

    this.scale = 1;
    this.scaleStep = 0.4;
    this.scaleMin = 1;
    this.scaleMax = 5;

    this.originX = 0; // translation X
    this.originY = 0; // translation Y
    this.startX = 0; // drag start
    this.startY = 0;
    this.isDragging = false;

    this.initPreviews();
    this.initZoom();
    this.initWheelZoom();
    this.initDrag();
    this.initOpenFromSlider();
  }

  initPreviews() {
    this.previews.forEach((preview) => {
      preview.addEventListener('click', () => {
        this.changeMainMedia(preview);
        this.setActivePreview(preview.dataset.index);
      });
    });
  }

  initZoom() {
    // Zoom In
    this.buttonZoomIn.addEventListener('click', () => {
      if (this.isVideoActive()) return;
      if (this.scale < this.scaleMax) {
        this.scale += this.scaleStep;
        this.updateTransform();
      }
    });
    // Zoom Out
    this.buttonZoomOut.addEventListener('click', () => {
      if (this.isVideoActive()) return;
      if (this.scale > this.scaleMin) {
        this.scale -= this.scaleStep;
        this.updateTransform();
      }
    });
  }

  initWheelZoom() {
    this.mainMediaWrapper.addEventListener(
      'wheel',
      (e) => {
        if (this.isVideoActive()) return;

        e.preventDefault();

        const delta = e.deltaY;

        if (delta < 0 && this.scale < this.scaleMax) {
          // Scroll up → zoom in
          this.scale += this.scaleStep;
        } else if (delta > 0 && this.scale > this.scaleMin) {
          // Scroll down → zoom out
          this.scale -= this.scaleStep;
        }

        this.scale = Math.min(this.scaleMax, Math.max(this.scaleMin, this.scale));

        this.updateTransform();
      },
      { passive: false },
    );
  }

  initDrag() {
    // Desktop drag
    this.mainMediaWrapper.addEventListener('mousedown', (e) => {
      if (this.isVideoActive()) return;

      this.isDragging = true;
      this.startX = e.clientX - this.originX;
      this.startY = e.clientY - this.originY;
      this.mainMediaWrapper.classList.add('cursor-grabbing');
      this.mainMediaWrapper.classList.remove('cursor-grab');
    });

    document.addEventListener('mouseup', () => {
      this.isDragging = false;
      this.mainMediaWrapper.classList.add('cursor-grab');
      this.mainMediaWrapper.classList.remove('cursor-grabbing');
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.isDragging) return;
      this.handleMove(e.clientX, e.clientY);
    });

    // Mobile drag
    this.mainMediaWrapper.addEventListener(
      'touchstart',
      (e) => {
        if (this.isVideoActive()) return;
        if (e.touches.length !== 1) return; // ignore multi-touch

        this.isDragging = true;
        const touch = e.touches[0];
        this.startX = touch.clientX - this.originX;
        this.startY = touch.clientY - this.originY;
      },
      { passive: true },
    );

    this.mainMediaWrapper.addEventListener('touchend', () => {
      this.isDragging = false;
    });

    this.mainMediaWrapper.addEventListener(
      'touchmove',
      (e) => {
        if (!this.isDragging || e.touches.length !== 1) return;
        const touch = e.touches[0];
        this.handleMove(touch.clientX, touch.clientY);
      },
      { passive: false },
    ); // false pour pouvoir empêcher scroll si nécessaire
  }

  changeMainMedia(button) {
    const { type, fullSrc, index } = button.dataset;

    if (this.loader) this.loader.classList.remove('hidden');

    if (type === 'video') {
      this.loadVideo(index);
    } else {
      this.loadImage(fullSrc);
    }
  }

  loadImage(fullSrc) {
    if (!fullSrc) return;

    const img = new Image();
    img.src = fullSrc;

    img.onload = () => {
      this.stopCurrentVideo();

      let mediaEl = this.mainMediaWrapper.querySelector('.js-main-media');
      if (!mediaEl || mediaEl.tagName !== 'IMG') {
        mediaEl = document.createElement('img');
        mediaEl.className = 'js-main-media select-none pointer-events-none w-full h-full object-contain';
        this.mainMediaWrapper.replaceChildren(mediaEl);
        this.mainMedia = mediaEl;
      }

      this.mainMedia.src = fullSrc;
      this.mainMedia.srcset = `${fullSrc} 1900w`;
      this.mainMedia.sizes = '100vw';

      this.scale = 1;
      this.originX = 0;
      this.originY = 0;
      this.updateTransform();

      if (this.loader) this.loader.classList.add('hidden');
    };
  }

  loadVideo(index) {
    this.stopCurrentVideo();

    const template = this.querySelector(`template[data-video-template][data-index="${index}"]`);
    if (!template) return;

    this.mainMediaWrapper.replaceChildren(template.content.cloneNode(true));
    this.mainMedia = this.mainMediaWrapper.querySelector('.js-main-media');

    // Force le navigateur à initialiser proprement l'état de lecture
    this.mainMedia.load();

    this.scale = 1;
    this.originX = 0;
    this.originY = 0;
    this.updateTransform();

    this.mainMedia.play();

    if (this.loader) this.loader.classList.add('hidden');
  }

  stopCurrentVideo() {
    const currentVideo = this.mainMediaWrapper.querySelector('video');
    if (currentVideo && !currentVideo.paused) {
      currentVideo.pause();
    }
  }

  isVideoActive() {
    return this.mainMedia && this.mainMedia.tagName === 'VIDEO';
  }

  // Move handler (shared desktop + mobile)
  handleMove(clientX, clientY) {
    this.originX = clientX - this.startX;
    this.originY = clientY - this.startY;

    const rect = this.mainMediaWrapper.getBoundingClientRect();
    const mediaWidth = this.mainMedia.offsetWidth * this.scale;
    const mediaHeight = this.mainMedia.offsetHeight * this.scale;

    const maxX = Math.max((mediaWidth - rect.width) / 2, 0);
    const maxY = Math.max((mediaHeight - rect.height) / 2, 0);

    this.originX = Math.min(maxX, Math.max(this.originX, -maxX));
    this.originY = Math.min(maxY, Math.max(this.originY, -maxY));

    this.updateTransform();
  }

  updateTransform() {
    if (this.isVideoActive()) return;
    this.mainMedia.style.transform = `scale(${this.scale}) translate(${this.originX}px, ${this.originY}px)`;
  }

  setActivePreview(index) {
    const targetIndex = String(index);

    this.previews.forEach((preview) => {
      if (preview.dataset.index === targetIndex) {
        preview.setAttribute('data-active', '');
      } else {
        preview.removeAttribute('data-active');
      }
    });
  }

  initOpenFromSlider() {
    const openers = document.querySelectorAll('.js-product-slideshow-item');

    openers.forEach((opener) => {
      opener.addEventListener('click', () => {
        const { type, fullSrc, index } = opener.dataset;
        if (isNaN(Number(index))) return;
        if (type !== 'video' && !fullSrc) return;

        this.changeMainMedia(opener);
        this.setActivePreview(index);
      });
    });
  }
}
customElements.define('modal-product-slideshow', ModalProductSlideshow);

/**
 * PRODUCT SLIDESHOW
 */
class ProductSlideshow extends HTMLElement {
  constructor() {
    super();
    this.previews = this.querySelectorAll('.js-preview-media');
    this.itemsWrapper = this.querySelector('.js-product-slideshow-container');
    this.items = this.querySelectorAll('.js-product-slideshow-item');

    this.slideWidth = this.items[0]?.clientWidth || 0;

    this.initPreviews();
  }

  initPreviews() {
    this.previews.forEach((preview) => {
      preview.addEventListener('click', () => {
        if (!this.isActive(preview)) {
          this.removePreviewActive();
          this.addPreviewActive(preview);
          this.scrollSlide(preview);
        }
      });
    });
  }

  isActive(element) {
    return element.hasAttribute('data-active');
  }

  removePreviewActive() {
    this.previews.forEach((preview) => {
      preview.removeAttribute('data-active');
    });
  }

  addPreviewActive(element) {
    element.setAttribute('data-active', '');
  }

  scrollSlide(element) {
    const slideIndex = element.dataset.index;
    const newPosition = this.slideWidth * Number(slideIndex);
    this.itemsWrapper.scrollTo({ left: newPosition, behavior: 'smooth' });
  }
}
customElements.define('product-slideshow', ProductSlideshow);

/**
 * SEE MORE SECTION
 */
class SeeMoreSection extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('.js-button');
    const items = this.querySelectorAll('.js-item');
    if (!button || !items) return;

    button.addEventListener('click', () => {
      items.forEach((item) => {
        item.classList.remove('hidden');
      });
      button.classList.add('hidden');
    });
  }
}
customElements.define('see-more-section', SeeMoreSection);
